import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ClipboardCheck, ClipboardList, ListChecks, Bell, AlertTriangle, FileBarChart, Building2, Refrigerator, Users, Settings, LogOut, Menu, X, Thermometer } from "lucide-react";
import { Brand } from "@/components/common";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/tareas", icon: ListChecks, label: "Tareas" },
  { to: "/admin/controles", icon: Thermometer, label: "Temperaturas" },
  { to: "/admin/planillas", icon: ClipboardList, label: "Planillas (Beta)" },
  { to: "/admin/alertas", icon: Bell, label: "Alertas" },
  { to: "/admin/incidencias", icon: AlertTriangle, label: "Incidencias" },
  { to: "/admin/reportes", icon: FileBarChart, label: "Reportes" },
  { to: "/admin/locales", icon: Building2, label: "Locales" },
  { to: "/admin/equipos", icon: Refrigerator, label: "Equipos" },
  { to: "/admin/usuarios", icon: Users, label: "Usuarios" },
  { to: "/admin/configuracion", icon: Settings, label: "Configuración" },
];

function SidebarContent({ user, onNav, onLogout }) {
  return (
    <div className="flex h-full flex-col bg-[#2563EB]">
      <div className="px-5 py-5"><Brand variant="onPrimary" subtitle={user?.organization_name} /></div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNav} data-testid={`nav-${n.label.toLowerCase()}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#1D4ED8] text-white shadow-sm ring-1 ring-white/15" : "text-white/85 hover:bg-[#1D4ED8] hover:text-white"}`}>
            <n.icon className="h-[18px] w-[18px]" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/15 p-3">
        <div className="flex items-center gap-3 rounded-xl px-3 py-2">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-sm font-semibold text-white">{user?.name?.[0]}</div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{user?.name}</p>
            <p className="truncate text-xs text-white/60">{user?.email}</p>
          </div>
          <button data-testid="logout-btn" onClick={onLogout} className="grid h-9 w-9 place-items-center rounded-full hover:bg-white/15"><LogOut className="h-4 w-4 text-white/80" /></button>
        </div>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const onLogout = () => { logout(); nav("/login"); };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-64 shrink-0 bg-[#2563EB] lg:block"><div className="sticky top-0 h-screen"><SidebarContent user={user} onLogout={onLogout} /></div></aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 bg-[#2563EB]">
            <button className="absolute right-3 top-4 z-10 text-white/80" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            <SidebarContent user={user} onNav={() => setOpen(false)} onLogout={onLogout} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-white/80 px-4 py-3 backdrop-blur lg:px-8">
          <div className="flex items-center gap-2">
            <button className="lg:hidden" onClick={() => setOpen(true)}><Menu className="h-6 w-6" /></button>
            <span className="lg:hidden"><Brand subtitle={user?.organization_name} /></span>
          </div>
          <NotificationBell />
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 lg:px-8"><Outlet /></main>
      </div>
    </div>
  );
}
